import { noop } from 'lodash';
import { createContext, Dispatch, SetStateAction, useContext } from 'react';

import { useGuardedContext } from '@/hooks/useGuardedContext';
import {
  AiOnboardingModal_EntityFragment,
  AiOnboardingModal_PossiblePrincipalClientProfileFragment,
} from '@/modules/aiOnboarding/AIOnboardingModal/graphql/aiOnboardingModal.generated';
import { AssetIntegrationProviders } from '@/modules/assetProviderIntegrations/shared/constants';
import { PrimaryClientDropdown_PossibleGrantorFragment } from '@/modules/entities/inputs/PrimaryClientDropdown/graphql/PrimaryClientDropdown.generated';
import {
  GetClientDetails_ClientOrganizationFragment,
  GetClientDetails_ClientProfileFragment,
} from '@/modules/household/contexts/graphql/GetClientDetails.generated';
import { RecentClientFragment } from '@/modules/recents/RecentEntitiesSidebar/graphql/RecentEntitiesSidebar.generated';
import {
  DocumentType,
  HouseholdBillableKind,
  IntegrationClientKind,
  StateCode,
} from '@/types/schema';

export interface ContextPrimaryClient
  extends AiOnboardingModal_PossiblePrincipalClientProfileFragment {
  id: string;
  displayName: string;
  displayGivenName: string;
  preferredName?: string | null;
  lastName: string;
  stateCode?: StateCode;
  createdAt?: Date;
  updatedAt: Date;
  isPrimary: boolean;
}

export interface PossibleHouseholdTrustees {
  clients: GetClientDetails_ClientProfileFragment[];
  organizations: GetClientDetails_ClientOrganizationFragment[];
}

export interface PossibleHouseholdBeneficiaries
  extends PossibleHouseholdTrustees {
  entities: AiOnboardingModal_EntityFragment[];
}

export interface HouseholdDetailsValue {
  loading: boolean;

  householdId: string | null;

  createdAt: Date | null;

  displayName: string | null;

  isPrivateHousehold: boolean;

  primaryClients: ContextPrimaryClient[] | null;

  defaultFirstToDieClientId?: string | null;

  possibleGrantors: PrimaryClientDropdown_PossibleGrantorFragment[] | null;

  possibleTrustees: PossibleHouseholdTrustees | null;

  possibleBeneficiaries: PossibleHouseholdBeneficiaries | null;

  entities: AiOnboardingModal_EntityFragment[] | null;

  isTwoClientHousehold: boolean;

  isHouseholdLinkedToBlackDiamond: boolean;
  isHouseholdLinkedToAddepar: boolean;
  isHouseholdLinkedToAssetProvider: boolean;
  householdIntegrationLink: IntegrationClientKind | null;
  linkedHouseholdAssetProvider: AssetIntegrationProviders | null;

  billableKind: HouseholdBillableKind | null;

  showGenerateV2SuggestionsPrompt: boolean;

  recentClients: RecentClientFragment[];

  householdNotFound: boolean;

  /**
   * Documents in this household. Documents that have been soft-deleted
   * will not be included in this array.
   */
  documents: {
    id: string;
    name: string;
    type: DocumentType;
    file: { id: string };
  }[];
}

export type HouseholdDetailsContext = HouseholdDetailsValue;

export interface HouseholdDetailsUpdaters {
  setLoading: Dispatch<SetStateAction<HouseholdDetailsValue['loading']>>;
  setHouseholdId: Dispatch<
    SetStateAction<HouseholdDetailsValue['householdId']>
  >;
  setCreatedAt: Dispatch<SetStateAction<HouseholdDetailsValue['createdAt']>>;
  setDisplayName: Dispatch<
    SetStateAction<HouseholdDetailsValue['displayName']>
  >;
  setIsPrivateHousehold: Dispatch<
    SetStateAction<HouseholdDetailsValue['isPrivateHousehold']>
  >;
  setPrimaryClients: Dispatch<
    SetStateAction<HouseholdDetailsValue['primaryClients']>
  >;
  setDefaultFirstToDieClientId: Dispatch<
    SetStateAction<HouseholdDetailsValue['defaultFirstToDieClientId']>
  >;
  setPossibleGrantors: Dispatch<
    SetStateAction<HouseholdDetailsValue['possibleGrantors']>
  >;
  setPossibleTrustees: Dispatch<
    SetStateAction<HouseholdDetailsValue['possibleTrustees']>
  >;
  setPossibleBeneficiaries: Dispatch<
    SetStateAction<HouseholdDetailsValue['possibleBeneficiaries']>
  >;
  setEntities: Dispatch<SetStateAction<HouseholdDetailsValue['entities']>>;
  setIsTwoClientHousehold: Dispatch<
    SetStateAction<HouseholdDetailsValue['isTwoClientHousehold']>
  >;
  setHouseholdIntegrationLink: Dispatch<
    SetStateAction<HouseholdDetailsValue['householdIntegrationLink']>
  >;
  setBillableKind: Dispatch<
    SetStateAction<HouseholdDetailsValue['billableKind']>
  >;
  setShowGenerateV2SuggestionsPrompt: Dispatch<
    SetStateAction<HouseholdDetailsValue['showGenerateV2SuggestionsPrompt']>
  >;
  setRecentClients: Dispatch<
    SetStateAction<HouseholdDetailsValue['recentClients']>
  >;
  setHouseholdNotFound: Dispatch<
    SetStateAction<HouseholdDetailsValue['householdNotFound']>
  >;
  setDocuments: Dispatch<SetStateAction<HouseholdDetailsValue['documents']>>;
}

export const HouseholdDetailsContext = createContext<HouseholdDetailsValue>({
  loading: false,

  householdId: null,

  createdAt: null,

  displayName: null,

  isPrivateHousehold: false,

  primaryClients: null,

  defaultFirstToDieClientId: null,

  possibleGrantors: null,

  possibleTrustees: null,

  possibleBeneficiaries: null,

  entities: null,

  isTwoClientHousehold: false,

  isHouseholdLinkedToBlackDiamond: false,
  isHouseholdLinkedToAddepar: false,
  isHouseholdLinkedToAssetProvider: false,
  householdIntegrationLink: null,
  linkedHouseholdAssetProvider: null,

  billableKind: null,

  showGenerateV2SuggestionsPrompt: false,

  recentClients: [],

  householdNotFound: false,

  documents: [],
});

export const HouseholdDetailsUpdaterContext =
  createContext<HouseholdDetailsUpdaters>({
    setLoading: noop,
    setHouseholdId: () => null,
    setCreatedAt: () => null,
    setDisplayName: () => null,
    setIsPrivateHousehold: () => null,
    setPrimaryClients: () => null,
    setDefaultFirstToDieClientId: () => null,
    setPossibleGrantors: () => null,
    setPossibleTrustees: () => null,
    setPossibleBeneficiaries: () => null,
    setEntities: () => null,
    setIsTwoClientHousehold: () => null,
    setHouseholdIntegrationLink: () => null,
    setBillableKind: () => null,
    setShowGenerateV2SuggestionsPrompt: () => null,
    setRecentClients: () => null,
    setHouseholdNotFound: () => null,
    setDocuments: () => null,
  });

export const useHouseholdDetailsContext = () => {
  return useGuardedContext(HouseholdDetailsContext, 'ClientDetailsProvider');
};

export const useHouseholdDetailsUpdaterContext = () => {
  return useGuardedContext(
    HouseholdDetailsUpdaterContext,
    'ClientDetailsProvider'
  );
};

export const useUnguardedHouseholdDetailsContext = () => {
  return useContext(HouseholdDetailsContext);
};
